import { SkeletonList } from '@batiflow/ui';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  __css,
  tokens,
} from '@fluentui/react-components';
import { DocumentCheckmark20Regular } from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DocumentDetail } from '../document-model';
import { usePanelStyles } from '../panel-styles';
import { ApprovalStatusBadge } from './approval-badges';
import { useDocumentFlows } from './approvals-api';
import { latestFlow } from './approvals-model';
import { FlowCard } from './flow-card';
import { RequestApprovalDialog } from './request-approval-dialog';

const useStyles = __css({"flows":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f13377fj"},"past":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"pastHeading":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fy9rknc","Bhrd7zp":"fl43uef","sj55zd":"f11d4kpn"}});

/** The state of the latest approval at the top of the document panel (nothing when there is none). */
export function ApprovalPanelBadge({
  spaceId,
  documentId,
}: {
  spaceId: string;
  documentId: string;
}) {
  const flows = useDocumentFlows(spaceId, documentId);
  const latest = latestFlow(flows.data?.items ?? []);
  return latest ? <ApprovalStatusBadge status={latest.status} /> : null;
}

export interface ApprovalsSectionProps {
  /** Null in another organisation's space: requesting a visa needs the organisation's members. */
  orgId: string | null;
  document: DocumentDetail;
}

/**
 * The "Approval" section of the document panel: the flow in progress and the earlier ones, each
 * with its stepper, and "Request approval" for a caller who may ask for it while no flow runs.
 */
export function ApprovalsSection({ orgId, document }: ApprovalsSectionProps) {
  const { t } = useTranslation('documents');
  const panel = usePanelStyles();
  const styles = useStyles();
  const flows = useDocumentFlows(document.spaceId, document.id);
  const [requesting, setRequesting] = useState(false);

  const items = flows.data?.items ?? [];
  const running = items.find((flow) => flow.status === 'in_progress');
  const past = items.filter((flow) => flow !== running);
  const canRequest =
    orgId !== null &&
    document._permissions.includes('can_request_approval') &&
    !document.lockedByFlowId &&
    !running;

  let body;
  if (flows.isError && !flows.data) {
    body = (
      <MessageBar intent="error" layout="multiline">
        <MessageBarBody>{t('approvals.section.loadFailed')}</MessageBarBody>
        <MessageBarActions>
          <Button
            onClick={() => {
              void flows.refetch();
            }}
          >
            {t('common:actions.retry')}
          </Button>
        </MessageBarActions>
      </MessageBar>
    );
  } else if (!flows.data) {
    body = <SkeletonList count={2} />;
  } else if (items.length === 0) {
    body = <p className={panel.muted}>{t('approvals.section.none')}</p>;
  } else {
    body = (
      <div className={styles.flows}>
        {running ? <FlowCard flow={running} /> : null}
        {past.length > 0 ? (
          <div className={styles.past}>
            <h4 className={styles.pastHeading}>{t('approvals.section.past')}</h4>
            {past.map((flow) => (
              <FlowCard key={flow.id} flow={flow} />
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <section className={panel.section} aria-label={t('approvals.section.region')}>
      <div className={panel.sectionHead}>
        <h3 className={panel.heading}>{t('approvals.section.title')}</h3>
        {canRequest ? (
          <Button
            size="small"
            appearance="primary"
            icon={<DocumentCheckmark20Regular />}
            onClick={() => {
              setRequesting(true);
            }}
          >
            {t('approvals.section.request')}
          </Button>
        ) : null}
      </div>
      {body}
      {requesting && orgId !== null ? (
        <RequestApprovalDialog
          orgId={orgId}
          document={document}
          onClose={() => {
            setRequesting(false);
          }}
        />
      ) : null}
    </section>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/approvals/approvals-section.griffel.css";